/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState, useMemo, type ReactNode } from 'react'
import { m } from 'motion/react'
import slugify from 'slugify'
import { vercelSplit } from '@web/cms'
import * as Tabs from '@radix-ui/react-tabs'
import { Dropdown, Text } from '@hashicorp/mds-react/components'
import type { VerticalListPanelFieldsFragment } from 'types/dato'
import { Layout } from 'components/materia/layout'
import HeadingText from 'components/materia/sub-blocks/heading-text'
import ImageTextPanel from './image-text-panel'
import VideoPanel from './video-panel'
import s from './style.module.css'

export default function VerticalListPanel({
	layout,
	eyebrow,
	heading,
	tabs: tabsWithoutValue,
}: VerticalListPanelFieldsFragment) {
	// Add value to each tab that conforms to aria rules
	const tabs = useMemo(
		() =>
			tabsWithoutValue.map((tab) => ({
				...tab,
				value: slugify(vercelSplit(tab.name), { replacement: '' }),
			})),
		[tabsWithoutValue],
	)

	const [selectedTab, setSelectedTab] = useState(tabs[0].value)

	return (
		<Layout layout={layout}>
			<div className={s.verticalListPanel}>
				<HeadingText
					eyebrow={eyebrow}
					heading={heading}
					className={s.headingContainer}
				/>
				<MobileDropdown
					tabs={tabs.map(({ name, value }) => ({ name, value }))}
					setSelectedTab={setSelectedTab}
					selectedTab={selectedTab}
				/>
				<Tabs.Root
					defaultValue={tabs[0].value}
					orientation="vertical"
					className={s.tabsRoot}
					value={selectedTab}
					onValueChange={setSelectedTab}
					activationMode="manual"
				>
					<Tabs.List className={s.tabsList}>
						{tabs.map(({ name, value }) => (
							<TabsTrigger
								key={`tab-${value}`}
								name={name}
								selectedTab={selectedTab}
								value={value}
							/>
						))}
					</Tabs.List>
					<div className={s.panels}>
						{tabs.map((tab, idx) => (
							<TabsContent
								{...tab}
								key={`tab-panel-${tab.value}`}
								selectedTab={selectedTab}
								index={idx}
							/>
						))}
					</div>
				</Tabs.Root>
			</div>
		</Layout>
	)
}

type MobileDropdownProps = {
	tabs: Array<{ name: string; value: string }>
	selectedTab: string
	setSelectedTab: (tab: string) => void
}

function MobileDropdown({
	tabs,
	selectedTab,
	setSelectedTab,
}: MobileDropdownProps) {
	return (
		<div className={s.mobileDropdown}>
			<Dropdown.Root isFullWidth>
				<Dropdown.ToggleButton
					text={tabs.find(({ value }) => value === selectedTab).name}
					isFullWidth
					color="secondary"
				/>
				<Dropdown.Content width="100%">
					<Dropdown.List>
						{tabs.map(({ name, value }) => (
							<Dropdown.ListItem.Interactive
								onClick={() => setSelectedTab(value)}
								key={`dropdownItem${name}`}
								text={name}
							/>
						))}
					</Dropdown.List>
				</Dropdown.Content>
			</Dropdown.Root>
		</div>
	)
}

type TabsTriggerProps = {
	name: string
	value: string
	selectedTab: string
}

function TabsTrigger({ name, value, selectedTab }: TabsTriggerProps) {
	return (
		<Tabs.Trigger
			className={s.tabListTrigger}
			value={value}
			key={`tab-${vercelSplit(name)}`}
		>
			<Text.Body
				color={selectedTab === value ? 'action-active' : 'faint'}
				size="300"
				className={s.tabListTriggerText}
			>
				{name}
			</Text.Body>
		</Tabs.Trigger>
	)
}

type TabsContentProps = VerticalListPanelFieldsFragment['tabs'][0] & {
	selectedTab: string
	value: string
	index: number
}

function TabsContent({
	selectedTab,
	name,
	value,
	panel,
	index,
}: TabsContentProps): JSX.Element {
	const isActive = selectedTab === value
	return (
		<AnimatedPanel
			name={name}
			selectedTab={selectedTab}
			index={index}
			value={value}
		>
			<Tabs.Content
				value={value}
				key={`tab-panel-${vercelSplit(name)}`}
				tabIndex={-1}
				forceMount
			>
				<RenderPanel panel={panel[0]} isActive={isActive} />
			</Tabs.Content>
		</AnimatedPanel>
	)
}

export const RenderPanel = ({
	panel,
	isActive,
}: {
	panel: TabsContentProps['panel'][number]
	isActive?: boolean
}) => {
	switch (panel.__typename) {
		case 'TabsLeftTabPanelRecord':
			return <ImageTextPanel {...panel} />
		case 'TabsLeftTabVideoPanelRecord':
			return <VideoPanel isActive={isActive} {...panel} />
		default:
			return <></>
	}
}

const variants = {
	active: {
		opacity: 1,
		transition: {
			duration: 0.3,
			delay: 0.3,
		},
	},
	inactive: {
		opacity: 0,
		transition: {
			duration: 0.3,
		},
	},
}

type AnimatedPanelProps = {
	selectedTab: string
	name: string
	value: string
	index: number
	children: ReactNode
}

function AnimatedPanel({
	index,
	selectedTab,
	name,
	value,
	children,
}: AnimatedPanelProps) {
	const isActive = selectedTab === value
	return (
		<m.div
			key={index}
			className={s.panelWrapper}
			variants={variants}
			style={{ opacity: 0 }}
			initial={index === 0 ? 'visible' : 'hidden'}
			animate={isActive ? 'active' : 'inactive'}
			// @see https://github.com/WICG/inert/issues/58
			// I believe that this has been addressed in React 19. However, until we
			// upgrade to Next.js 15 (which supports React 19), I think we need to preserve this hack.
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			inert={!isActive ? 'true' : undefined}
		>
			{children}
		</m.div>
	)
}
